import { useEffect } from 'react';

/**
 * Dynamically synchronise the OS status bar (Android Chrome PWA + Capacitor
 * native) with the active app theme.
 *
 * Usage :
 *   useStatusBarTheme('dark');    // dark shell (#0B1220), white icons
 *   useStatusBarTheme('light');   // light shell (#FFFFFF), dark icons
 *   useStatusBarTheme('brand');   // Mibeko brand blue (#0F2845), white icons
 *
 * Effects :
 *  - <meta name="theme-color"> content is updated for all tags
 *    (light, dark, default) so Android Chrome + Samsung Internet PWA
 *    tint the status bar accordingly.
 *  - Capacitor `@capacitor/status-bar` is asked to change
 *    background + icon style on native iOS/Android builds.
 *  - A `data-statusbar-theme` attribute is set on <body> for debugging.
 *
 * On iOS PWA installed to home screen, the
 * `apple-mobile-web-app-status-bar-style` meta is fixed at install
 * time (`black-translucent`) — the safe-area zone painted by the body
 * background will follow this hook.
 */

const THEMES = {
  dark: { color: '#0B1220', style: 'Dark' }, // Style.Dark => white icons
  light: { color: '#FFFFFF', style: 'Light' }, // Style.Light => dark icons
  brand: { color: '#0F2845', style: 'Dark' },
};

async function applyToNativeStatusBar(themeKey) {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (!Capacitor?.isNativePlatform?.()) return;
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const t = THEMES[themeKey];
    if (!t) return;
    await StatusBar.setBackgroundColor({ color: t.color });
    await StatusBar.setStyle({ style: Style[t.style] });
  } catch {
    /* Not available in browser preview — safe to ignore. */
  }
}

export function useStatusBarTheme(themeKey = 'light') {
  useEffect(() => {
    const t = THEMES[themeKey] || THEMES.light;

    // 1) Update every <meta name="theme-color"> (including media variants)
    document.querySelectorAll('meta[name="theme-color"]').forEach((m) => {
      m.setAttribute('content', t.color);
    });

    // 2) Body background is already driven by the CSS variable --app-shell-bg
    //    via `[data-theme="…"]`. We only tag the body for debugging.
    document.body.setAttribute('data-statusbar-theme', themeKey);

    // 3) Sync the native Capacitor status bar (no-op on the web).
    applyToNativeStatusBar(themeKey);
  }, [themeKey]);
}

